import { computed } from 'vue';
import { IImport } from '@/models/imports.interface';
import { IResponse, IResponseStatuses, IResponseStatus } from '@/models/response.interface';
import cookieHandler from '@/infrastructure/cookie-handler';
import store from '@/store';
import { UTCDateTime } from '@studyportals/mb-platform-date-time';

class ImportedDataPresenter {
	private readonly tokenId: string;
	private readonly baseUrl: string;
	private readonly defaultErrorMessage = 'reason unknown.';

	constructor() {
		this.tokenId = cookieHandler.retrieveTokenIdCookie();
		this.baseUrl = webpackDefinitions.DATA_IMPORT_SYSTEM_BACKEND_URL;
	}

	private errorMessage = computed((): string => {
		return store.getters.errorMessage();
	});

	public async postCSV(csvContents: string): Promise<void> {
		const errorCategory = 'Upload failed:';
		this.resetErrorMessage(errorCategory);

		const body = {
			csv: csvContents
		};
		const options = {
			method: 'POST',
			body: JSON.stringify(body),
			headers: {
				Authorization: this.tokenId
			}
		};

		try {
			const response = await fetch(
				`${this.baseUrl}/upload/csv`,
				options as RequestInit
			);
			if (!response.ok) {
				store.mutations.setErrorMessage(`${errorCategory} ${this.defaultErrorMessage}`);
				return;
			}

			const result = await response.json() as IResponse;
			if (result.failed) {
				store.mutations.setErrorMessage(`${errorCategory} ${result.errorMsg}`);
			}
		} catch (error) {
			store.mutations.setErrorMessage(`${errorCategory} ${error as string}`);
		}
	}

	public async getAllUploads(): Promise<void> {
		const errorCategory = 'Statuses retrieval failed:';
		this.resetErrorMessage(errorCategory);

		const options = {
			method: 'GET',
			headers: {
				Authorization: this.tokenId
			}
		};

		try {
			const response = await fetch(
				`${this.baseUrl}/uploads`,
				options as RequestInit
			);
			if (!response.ok) {
				store.mutations.setErrorMessage(`${errorCategory} ${this.defaultErrorMessage}`);
				return;
			}

			const result = await response.json() as IResponseStatuses;
			if (result.failed) {
				store.mutations.setErrorMessage(`${errorCategory} ${result.errorMsg}`);
				return;
			}

			const statuses = JSON.parse(result.statuses) as IImport[];
			const sortedStatuses = statuses.sort(
				(a, b) => {
					return new UTCDateTime(new Date(b.timestampStart)).getUnixTime() - new UTCDateTime(new Date(a.timestampStart)).getUnixTime()
				}
			);

			store.mutations.setImports(sortedStatuses);
		} catch (error) {
			store.mutations.setErrorMessage(`${errorCategory} ${error as string}`);
		}
	}

	public async getSpecificUpload(upload: IImport): Promise<void> {
		const errorCategory = 'Status retrieval failed:';
		this.resetErrorMessage(errorCategory);

		const options = {
			method: 'GET',
			headers: {
				Authorization: this.tokenId
			}
		};

		try {
			const response = await fetch(
				`${this.baseUrl}/uploads/${upload.id}`,
				options as RequestInit
			);
			if (!response.ok) {
				store.mutations.setErrorMessage(`${errorCategory} ${this.defaultErrorMessage}`);
				return;
			}

			const result = await response.json() as IResponseStatus;
			if (result.failed) {
				store.mutations.setErrorMessage(`${errorCategory} ${result.errorMsg}`);
				return;
			}

			const status = JSON.parse(result.status) as IImport;
			store.mutations.updateImport(status);
		} catch (error) {
			store.mutations.setErrorMessage(`${errorCategory} ${error as string}`);
		}
	}

	private resetErrorMessage(category: string): void {
		if (!this.errorMessage.value.includes(category)) {
			return;
		}

		store.mutations.setErrorMessage('');
	}
}

export default new ImportedDataPresenter();
